import React, { useEffect } from 'react';
import produce from 'immer';
import { type CustomButton, ImageType } from '@types';
import { Collapse } from '@components/Collapse';
import Title from '@components/Title';
import ImageContainer from '@components/ImageContainer';
import Image from '@components/Image';
import Input from '@components/Input';
import Button from '@components/Button';
import Tip from '@components/Tip';
import useInputHandlers from '@hooks/useInputHandelrs';
import useGetFeatureTitle from '@hooks/useGetFeatureTitle';
import { $editingCustomButton } from '@stores/editingStores';
import useTemplateFeature from '@hooks/useTemplateFeature';
import FeatureIcon from '@components/FeatureIcon';
import PanelBase from '../PanelBase';
import { selectLibCBIcons } from '../../mediaLib';
import type { PanelBaseProps } from '../types';
import useCustomButtonEditing from './useCustomButtonEditing';

interface Props extends PanelBaseProps<CustomButton[]> {
  groupIndex: number;
  isNew?: boolean;
}

function CustomGroupBase({
  defaultValue,
  onConfirm,
  groupIndex,
  isNew,
}: Props) {
  const getFTitle = useGetFeatureTitle();

  const { state, setState, isChanged } = useCustomButtonEditing(defaultValue);
  const group = state[groupIndex];
  const customButtonIcon = useTemplateFeature('customButtonIcon');

  useEffect(() => {
    $editingCustomButton.api.set(state);
    return () => {
      $editingCustomButton.api.set(null);
    };
  }, [state]);

  const titleHandlers = useInputHandlers((val) =>
    setState((draft) => {
      draft[groupIndex].groupTitle = val;
    })
  );

  const handleDelete = () => {
    onConfirm(
      produce(state, (draft) => {
        draft.splice(groupIndex, 1);
      })
    );
  };

  const handleConfirm = () => {
    onConfirm(state);
  };

  const handleAfterClose = () => {
    if (isNew) handleDelete();
  };

  return (
    <PanelBase
      title={<Title>分组设置</Title>}
      isChanged={isChanged}
      onDeleteClick={handleDelete}
      onConfirm={handleConfirm}
      onAfterClose={handleAfterClose}
    >
      {customButtonIcon && (
        <Collapse title="图标设置">
          <ImageContainer
            className="h-70"
            useResetIcon
            placeholder={<FeatureIcon type="_group" className="h-40 w-40" />}
            onDeleteClick={() =>
              setState((draft) => {
                draft[groupIndex].iconMediaId = 0;
                draft[groupIndex].iconMediaUrl = '';
              })
            }
          >
            {!!group.iconMediaUrl && (
              <Image
                className="h-40 w-40"
                src={group.iconMediaUrl}
                type={
                  group.iconMediaId === 0 ? ImageType.sysMedia : ImageType.media
                }
              />
            )}
          </ImageContainer>
          <div className="mt-10 flex items-center justify-between">
            <Tip>建议尺寸：80 X 80</Tip>
            <Button
              size="sm"
              onClick={async () => {
                const { libItems, sysItems } = await selectLibCBIcons();
                if (libItems) {
                  setState((draft) => {
                    draft[groupIndex].iconMediaId = libItems[0].id;
                    draft[groupIndex].iconMediaUrl = libItems[0].url;
                  });
                } else if (sysItems) {
                  setState((draft) => {
                    draft[groupIndex].iconMediaId = 0;
                    draft[groupIndex].iconMediaUrl = sysItems[0].url;
                  });
                }
              }}
            >
              编辑图标
            </Button>
          </div>
        </Collapse>
      )}
      <Collapse title="标题设置">
        <Input
          maxLength={50}
          scheme="dark"
          className="h-30"
          placeholder={getFTitle('_group')}
          value={group.groupTitle}
          {...titleHandlers}
        />
      </Collapse>
    </PanelBase>
  );
}

export default CustomGroupBase;
